/**
 * BloomEffect - unified bloom/glow for WebGL (SelectiveBloomEffect) and WebGPU (BloomNode).
 */

import { SelectiveBloomEffect } from 'postprocessing';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { convertToTexture, float, lessThan, mix, screenUV, select, texture, uniform, vec4 } from 'three/tsl';

import { type EffectContext, IPostProcessEffect, type WebGLEffectData, type WebGPUComposeContext, type WebGPUEffectNode } from '../IPostProcessEffect.js';
import { type BloomEffectConfig, DefaultPostprocessEffectConfigs, PostProcessPass, PostProcessRenderOrder } from '../PostProcessConfig.js';

import type BloomNode from 'three/addons/tsl/display/BloomNode.js';
import type { UniformNode } from 'three/webgpu';

/**
 * Unified Bloom effect for both WebGL and WebGPU.
 *
 * WebGL: Uses SelectiveBloomEffect from postprocessing library
 * WebGPU: Uses BloomNode from TSL
 */
export class BloomEffect extends IPostProcessEffect<BloomEffectConfig> {
  override readonly type = PostProcessPass.Bloom;
  override readonly order = PostProcessRenderOrder.Bloom;

  private hasExclusionsUniform: UniformNode<'bool', boolean> | null = null;

  override getDefaultConfig(): BloomEffectConfig {
    return { ...DefaultPostprocessEffectConfigs[PostProcessPass.Bloom] };
  }

  // ============= WebGL Implementation =============

  override createWebGLEffect(config: BloomEffectConfig, context: EffectContext): WebGLEffectData {
    const defaults = this.getDefaultConfig();
    const effect = new SelectiveBloomEffect(context.scene, context.camera, {
      intensity: config.strength ?? defaults.strength,
      luminanceThreshold: config.threshold ?? defaults.threshold,
      luminanceSmoothing: config.smoothing ?? defaults.smoothing,
      mipmapBlur: config.mipmapBlur ?? defaults.mipmapBlur,
      radius: config.radius ?? defaults.radius,
      levels: config.levels ?? defaults.levels,
    });

    effect.inverted = true;
    effect.ignoreBackground = config.ignoreBackground ?? defaults.ignoreBackground!;

    return { effects: [effect] };
  }

  override applyWebGLConfig(effectData: WebGLEffectData, config: BloomEffectConfig): void {
    const defaults = this.getDefaultConfig();
    const bloomEffect = effectData.effects[0] as SelectiveBloomEffect;

    if (bloomEffect) {
      bloomEffect.intensity = config.strength ?? defaults.strength!;
      bloomEffect.inverted = true;
      bloomEffect.ignoreBackground = config.ignoreBackground ?? defaults.ignoreBackground!;

      if (config.threshold !== undefined) {
        bloomEffect.luminanceMaterial.threshold = config.threshold;
      }
      if (config.smoothing !== undefined) {
        bloomEffect.luminanceMaterial.smoothing = config.smoothing;
      }
      if (config.mipmapBlur !== undefined) {
        bloomEffect.mipmapBlurPass.enabled = config.mipmapBlur;
      }
      if (config.radius !== undefined) {
        bloomEffect.mipmapBlurPass.radius = config.radius;
      }
      if (config.levels !== undefined) {
        bloomEffect.mipmapBlurPass.levels = config.levels;
      }
    }
  }

  // ============= WebGPU Implementation =============

  override createWebGPUNode(config: BloomEffectConfig, context: EffectContext): WebGPUEffectNode {
    const defaults = this.getDefaultConfig();
    const useSelectiveEmissive = config.selectiveEmissive ?? defaults.selectiveEmissive;
    const ignoreBackground = config.ignoreBackground ?? defaults.ignoreBackground;
    const hasExclusions = uniform(false);
    this.hasExclusionsUniform = hasExclusions;

    const bloomNode = bloom(
      null as any,
      config.strength ?? defaults.strength!,
      config.radius ?? defaults.radius!,
      config.threshold ?? defaults.threshold!
    );

    bloomNode.smoothWidth.value = config.smoothing ?? defaults.smoothing!;

    const maskTexture = context.exclusionMaskTexture;

    return {
      node: bloomNode,
      needsMRT: useSelectiveEmissive,
      mrtOutputs: useSelectiveEmissive ? ['emissive'] : undefined,
      compose: (outputNode: any, ctx: WebGPUComposeContext) => {
        if (useSelectiveEmissive) {
          const emissiveNode = ctx.getMRTTexture('emissive');
          if (emissiveNode) {
            bloomNode.inputNode = emissiveNode;
            bloomNode.threshold.value = 0.0;
            bloomNode.smoothWidth.value = 1.0;
          }
        } else {
          const sourceColorNode = ctx.bloomInputNode
            ? select(hasExclusions, ctx.bloomInputNode, outputNode)
            : outputNode;
          let bloomInput = sourceColorNode;

          if (ignoreBackground) {
            const mainDepthSample = convertToTexture(ctx.depthNode).sample(screenUV).r;
            const depthSample = ctx.bloomDepthNode
              ? select(hasExclusions, convertToTexture(ctx.bloomDepthNode).sample(screenUV).r, mainDepthSample)
              : mainDepthSample;
            const foregroundMask = lessThan(depthSample, float(0.9999));
            bloomInput = mix(vec4(0), sourceColorNode, select(foregroundMask, float(1), float(0)));
          }

          bloomNode.inputNode = bloomInput;
        }

        const exclusionMaskTexture = ctx.exclusionMaskTexture ?? maskTexture;
        if (!exclusionMaskTexture) {
          return outputNode.add(bloomNode);
        }

        const maskRaw = texture(exclusionMaskTexture, screenUV).r;
        const bloomScale = select(hasExclusions, float(1).sub(maskRaw), float(1));
        return outputNode.add(bloomNode.mul(bloomScale));
      },
    };
  }

  override applyWebGPUConfig(node: WebGPUEffectNode, config: BloomEffectConfig): void {
    const defaults = this.getDefaultConfig();
    const bloomNode = node.node as BloomNode;

    if (bloomNode) {
      bloomNode.strength.value = config.strength ?? defaults.strength!;
      bloomNode.radius.value = config.radius ?? defaults.radius!;
      bloomNode.threshold.value = config.threshold ?? defaults.threshold!;
      bloomNode.smoothWidth.value = config.smoothing ?? defaults.smoothing!;
    }
  }

  override preRender(context: EffectContext): void {
    if (!this.hasExclusionsUniform) return;

    if (context.sharedExclusionMaskTexture !== undefined) {
      this.hasExclusionsUniform.value = context.sharedHasExclusions === true;
      return;
    }

    this.hasExclusionsUniform.value = context.excludedObjects ? context.excludedObjects.size > 0 : false;
  }

  override requiresRebuild(oldConfig: BloomEffectConfig, newConfig: BloomEffectConfig): boolean {
    return (
      oldConfig.enabled !== newConfig.enabled ||
      oldConfig.selectiveEmissive !== newConfig.selectiveEmissive ||
      oldConfig.ignoreBackground !== newConfig.ignoreBackground
    );
  }

  override dispose(): void {
    this.hasExclusionsUniform = null;
  }
}
